import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { getApiUrl } from "../config";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true);
    
    try {
      const res = await fetch(getApiUrl("/api/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include"
      });
      
      const responseData = await res.json();
      
      if (!res.ok) {
        throw new Error(responseData.message || "Failed to send reset email");
      }
      
      setIsSuccess(true);
      toast({
        title: "Reset email sent",
        description: "Check your email for password reset instructions.",
        className: "bg-white text-black border-zinc-200",
      });
    } catch (error: any) {
      toast({
        title: "Failed to send reset email",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
        {/* Left column – Success message */}
        <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
            <div className="p-8 w-full text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold tracking-wide text-center text-gray-800 dark:text-white mb-4">
                Check Your Email
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We've sent password reset instructions to your email address. 
                Please check your inbox and follow the link to reset your password.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                The reset link will expire in 1 hour for security reasons.
              </p>
              <Button 
                onClick={() => window.location.href = '/login'}
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </div>
        </main>

        {/* Right column – Splash image */}
        <div className="w-full lg:w-1/2 relative hidden lg:block">
          <img src="/assets/login_spash.png" alt="Forgot password visual" className="w-full h-full object-cover" />
          <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
          <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
            <p className="font-bold">Connecting the blocks.</p>
            <p className="font-normal">Search. Discover. Analyze.</p>
          </div>
          <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
      {/* Left column – Forgot password form */}
      <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
          <div className="p-8 w-full">
            <div className="text-center mb-8">
              <Mail className="h-12 w-12 text-[#4A6CA7] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold tracking-wide text-gray-800 dark:text-white mb-2">
                Forgot Password?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address
                  </label>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="your@email.com" 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  variant="default"
                  className="w-full"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span>Sending...</span>
                    </span>
                  ) : "Send Reset Link"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <span className="text-gray-600 dark:text-gray-300 text-sm">Remember your password? </span>
          <a href="/login" className="text-[#4A6CA7] hover:text-[#0A51A8] text-sm font-medium underline">
            Back to Login
          </a>
        </div>
        
        <footer className="py-4 text-center text-gray-500 text-sm lg:w-full">
          © blockseer.com. All rights reserved. Version 1.0.0
        </footer>
      </main>

      {/* Right column – Splash image */}
      <div className="w-full lg:w-1/2 relative hidden lg:block">
        <img src="/assets/login_spash.png" alt="Forgot password visual" className="w-full h-full object-cover" />
        <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
        <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
          <p className="font-bold">Connecting the blocks.</p>
          <p className="font-normal">Search. Discover. Analyze.</p>
        </div>
        <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
      </div>
    </div>
  );
}